import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import Button from '@material-ui/core/Button';

import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import Link from '../../links/Link';
import { AdminPages, buildCreatePageTarget, buildPageId, buildPageTarget } from '../AdminLinkUtils';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { DESCRIPTION_FIELD, NAME_FIELD, TAGS_FIELD } from './BusinessServiceAdminConstants';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { formatTags } from '../../../utilities/StringUtilities';

const functionName = AdminPages.BusinessServices;

function getColumns() {
    return [
        {
            type: COLUMN_TYPES.LINK,
            mapping: NAME_FIELD,
            title: t`Name`,
            sortable: true,
            width: 300,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: DESCRIPTION_FIELD,
            title: t`Description`,
            sortable: true,
            maxWidth: 600,
            width: 400,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: TAGS_FIELD,
            title: t`Tags`,
            sortable: false,
            formatter: formatTags,
            width: 300,
        },
    ];
}

const initialSort = [{ property: NAME_FIELD, direction: SORT_ASC }];

const dataSource = {
    url: '/ipm/v1/admin/business-services',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [NAME_FIELD, DESCRIPTION_FIELD, TAGS_FIELD],
    },
};

export default function BusinessServiceAdminList() {
    const { deleteWrapper } = useFormDeleteWrapper();
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const formDeleteService = useFormDeleteService();
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();

    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });

    const handleConfirmedDelete = () => {
        const params = _.merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteSelection(`${dataSource.url}/selection/softDelete`, params, tableSelection),
            refresherFunc: refresher,
        })();
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    return (
        <AdminPage
            title={t`Business Services`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        disabled={actionsDisabled}
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                    />
                    <Button
                        component={Link}
                        to={buildCreatePageTarget(functionName)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Create</Trans>
                    </Button>
                </Actions>
            }
        >
            <DataTable
                tableId="admin-business-service-list"
                loader={loader}
                triggerRef={triggerRef}
                columns={getColumns()}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
